import React, { useState, useEffect } from "react";
import axios from "axios";
import { useBusinessPlan } from "../../App";
import { useCurrency } from "../../contexts/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import CostFormContent from "../cost/CostFormContent";
import { Tooltip } from "../ui/Tooltip";
import { 
  DollarSign, 
  Calendar,
  ArrowRight,
  ArrowLeft,
  Target,
  Lightbulb,
  Building2,
  Package,
  TrendingDown,
  Plus,
  Edit2,
  Trash2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COST_CATEGORIES = [
  { value: "fixed", label: "Fixed Costs" },
  { value: "variable", label: "Variable Costs" },
  { value: "semi_variable", label: "Semi-Variable Costs" },
  { value: "opex", label: "OpEx (Operating Expenses)" },
  { value: "capex", label: "CapEx (Capital Expenditures)" },
  { value: "cogs", label: "COGS (Cost of Goods Sold)" },
  { value: "direct", label: "Direct Costs" },
  { value: "indirect", label: "Indirect Costs" },
  { value: "financial", label: "Financial Costs" },
  { value: "tax", label: "Tax Costs" }
];

const CostActualsEntry = () => {
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext, displayCurrency } = useCurrency();
  
  // Step state
  const [currentStep, setCurrentStep] = useState(1);
  const [showActualsList, setShowActualsList] = useState(false);
  
  // Data states
  const [selectedCategory, setSelectedCategory] = useState("");
  const [costItems, setCostItems] = useState([]);
  const [isUnplannedCost, setIsUnplannedCost] = useState(false);
  const [showUnplannedDialog, setShowUnplannedDialog] = useState(false);
  const [costLibraries, setCostLibraries] = useState({});
  const [sfHierarchy, setSfHierarchy] = useState([]);
  const [financialLoans, setFinancialLoans] = useState([]);
  const [products, setProducts] = useState([]);
  const [unplannedCostForm, setUnplannedCostForm] = useState({
    item_name: '',
    cost_category: '',
    amount: '',
    cost_type: 'one_time',
    description: '',
    annual_growth_rate: '',
    growth_rates_per_year: {},
    use_per_year_rates: false,
    linked_objective_id: '',
    linked_initiative_id: '',
    linked_revenue_stream_id: '',
    fixed_component: '',
    variable_rate: '',
    fixed_portion: '',
    variable_basis: '',
    linked_product_ids: [],
    cost_type_detail: 'material',
    unit_cost: '',
    quantity_per_unit: '',
    allocation_method: '',
    allocation_base: '',
    shared_across_products: true,
    department: '',
    approval_required: false,
    budget_allocation: '',
    is_recurring: false,
    asset_name: '',
    useful_life_years: '',
    depreciation_method: 'straight_line',
    salvage_value: '',
    loan_reference_id: '',
    loan_name: '',
    interest_rate: '',
    tax_type: '',
    tax_rate: '',
    tax_base: 'revenue',
    owner: ''
  });
  const [selectedCostItem, setSelectedCostItem] = useState(null);
  const [context, setContext] = useState(null);
  const [savedActuals, setSavedActuals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingActual, setEditingActual] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [actualToDelete, setActualToDelete] = useState(null);
  
  // Form states
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [entryMethod, setEntryMethod] = useState("manual");
  const [dataSource, setDataSource] = useState("");
  const [actualSpent, setActualSpent] = useState("");
  const [notes, setNotes] = useState("");
  
  // Variance states
  const [budgetVariance, setBudgetVariance] = useState(null);
  const [ytdVariance, setYtdVariance] = useState(null);
  
  useEffect(() => {
    if (currentPlan?.id) {
      fetchSavedActuals();
      fetchSupportingData();
      initializeDateRange();
    }
  }, [currentPlan]);
  
  const fetchSupportingData = async () => {
    try {
      // Fetch SF hierarchy using the same endpoint as CostManagementV2
      const sfResponse = await axios.get(`${API}/v3/cost-management/sf-hierarchy/${currentPlan.id}`);
      setSfHierarchy(sfResponse.data?.hierarchy || []);
      
      // Fetch products
      const productsResponse = await axios.get(`${API}/v2/products-enhanced/${currentPlan.id}`);
      setProducts(productsResponse.data || []);
      
      // Fetch cost libraries
      const librariesResponse = await axios.get(`${API}/v3/cost-management/item-library`);
      setCostLibraries(librariesResponse.data?.all_libraries || {});
      
      // Fetch loans if needed
      try {
        const loansResponse = await axios.get(`${API}/v3/cost-management/financial-loan-sync/${currentPlan.id}`);
        setFinancialLoans(loansResponse.data?.loans || []);
      } catch (err) {
        console.log("No loans found");
        setFinancialLoans([]);
      }
    } catch (error) {
      console.error("Error fetching supporting data:", error);
      // Set defaults to prevent errors
      setSfHierarchy([]);
      setProducts([]);
      setCostLibraries({});
      setFinancialLoans([]);
    }
  };
  
  useEffect(() => {
    if (selectedCategory && currentPlan?.id) {
      fetchCostItems();
    }
  }, [selectedCategory, currentPlan]);
  
  const initializeDateRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const firstDay = `${year}-${month}-01`;
    const lastDay = new Date(year, now.getMonth() + 1, 0).toISOString().split('T')[0];
    
    setFromDate(firstDay);
    setToDate(lastDay);
  };
  
  const fetchCostItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/v3/cost-items/${currentPlan.id}`);
      
      // Filter by category - handle both "financial" and "financials", "tax" variations
      const filtered = response.data.filter(item => {
        const itemCategory = (item.cost_category || "").toLowerCase();
        const searchCategory = selectedCategory.toLowerCase();
        
        // Handle variations
        if (searchCategory === "financial" || searchCategory === "financials") {
          return itemCategory === "financial" || itemCategory === "financials";
        }
        if (searchCategory === "tax") {
          return itemCategory === "tax" || itemCategory === "taxes";
        }
        
        return itemCategory === searchCategory;
      });
      
      setCostItems(filtered);
      
      // Debug log
      console.log(`Found ${filtered.length} items in category ${selectedCategory}`, filtered);
      
    } catch (error) {
      console.error("Error fetching cost items:", error);
      toast.error("Failed to fetch cost items");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCostItemContext = async (costItemId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/v2/cost-management/cost-item/${costItemId}/full-context`,
        {
          params: { from_date: fromDate, to_date: toDate }
        }
      );
      setContext(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching cost context:", error);
      toast.error("Failed to fetch cost context");
      return null;
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSavedActuals = async () => {
    try {
      const response = await axios.get(`${API}/actual-data/costs-v2/${currentPlan.id}`);
      setSavedActuals(response.data || []);
    } catch (error) {
      console.error("Error fetching saved actuals:", error);
    }
  };
  
  const handleCostItemSelect = async (costItem) => {
    setSelectedCostItem(costItem);
    setIsUnplannedCost(false);
    const contextData = await fetchCostItemContext(costItem.id);
    if (contextData) {
      setCurrentStep(2);
    }
  };
  
  const handleUnplannedCostSelect = () => {
    // Initialize form with selected category
    setUnplannedCostForm({
      ...unplannedCostForm,
      cost_category: selectedCategory
    });
    setShowUnplannedDialog(true);
  };
  
  const handleSaveUnplannedCost = () => {
    if (!unplannedCostForm.item_name || !unplannedCostForm.amount) {
      toast.error("Please enter cost name and amount");
      return;
    }
    
    setIsUnplannedCost(true);
    setSelectedCostItem(null);
    setContext({
      cost_item: unplannedCostForm,
      budget: {
        budgeted_amount: 0,
        annual_budget: 0,
        ytd_budget: 0,
        ytd_spent: 0,
        remaining: 0
      }
    });
    
    // Auto-populate the actual spent with the amount from CM form
    setActualSpent(unplannedCostForm.amount.toString());
    
    setShowUnplannedDialog(false);
    setCurrentStep(2);
  };
  
  const getDaysInRange = () => {
    if (!fromDate || !toDate) return 0;
    const from = new Date(fromDate);
    const to = new Date(toDate);
    const diffTime = Math.abs(to - from);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };
  
  const getPeriodLabel = () => {
    const days = getDaysInRange();
    if (days === 1) return "Single Day";
    if (days === 7) return "Weekly";
    if (days >= 28 && days <= 31) return "Monthly";
    if (days >= 89 && days <= 92) return "Quarterly";
    if (days >= 365 && days <= 366) return "Yearly";
    return `${days} Days`;
  };
  
  const calculateVariance = (actual, planned) => {
    if (!planned || planned === 0) return 0;
    return (((actual - planned) / planned) * 100).toFixed(2);
  };
  
  useEffect(() => {
    if (context && actualSpent) {
      const spent = parseFloat(actualSpent);
      setBudgetVariance(calculateVariance(spent, context.budget?.budgeted_amount || 0));
      
      const newYtdSpent = (context.budget?.ytd_spent || 0) + spent;
      setYtdVariance(calculateVariance(newYtdSpent, context.budget?.ytd_budget || 0));
    }
  }, [actualSpent, context]);
  
  const handleSaveCostActual = async () => {
    if (!actualSpent || !fromDate || !toDate) {
      toast.error("Please enter actual spent amount and date range");
      return;
    }
    
    try {
      setLoading(true);
      
      const payload = {
        plan_id: currentPlan.id,
        cost_item_id: isUnplannedCost ? null : selectedCostItem.id,
        from_date: fromDate,
        to_date: toDate,
        entry_method: entryMethod,
        data_source: dataSource || null,
        actual_spent: parseFloat(actualSpent),
        notes,
        is_unplanned: isUnplannedCost,
        unplanned_details: isUnplannedCost ? unplannedCostForm : null
      };
      
      await axios.post(`${API}/actual-data/costs-v2`, payload);
      
      toast.success("Cost actual saved successfully!");
      
      await fetchSavedActuals();
      
      // Reset form
      setCurrentStep(1);
      setSelectedCategory("");
      setSelectedCostItem(null);
      setContext(null);
      setIsUnplannedCost(false);
      setEditingActual(null);
      setUnplannedCostForm({
        item_name: '',
        cost_category: '',
        amount: '',
        cost_type: 'one_time',
        description: '',
        annual_growth_rate: '',
        growth_rates_per_year: {},
        use_per_year_rates: false,
        linked_objective_id: '',
        linked_initiative_id: '',
        linked_revenue_stream_id: '',
        fixed_component: '',
        variable_rate: '',
        fixed_portion: '',
        variable_basis: '',
        linked_product_ids: [],
        cost_type_detail: 'material',
        unit_cost: '',
        quantity_per_unit: '',
        allocation_method: '',
        allocation_base: '',
        shared_across_products: true,
        department: '',
        approval_required: false,
        budget_allocation: '',
        is_recurring: false,
        asset_name: '',
        useful_life_years: '',
        depreciation_method: 'straight_line',
        salvage_value: '',
        loan_reference_id: '',
        loan_name: '',
        interest_rate: '',
        tax_type: '',
        tax_rate: '',
        tax_base: 'revenue',
        owner: ''
      });
      setActualSpent("");
      setNotes("");
      setDataSource("");
      setBudgetVariance(null);
      setYtdVariance(null);
      
    } catch (error) {
      console.error("Error saving cost actual:", error);
      toast.error(error.response?.data?.detail || "Failed to save cost actual");
    } finally {
      setLoading(false);
    }
  };

  
  const handleEditActual = async (actual) => {
    // Populate form with existing data
    setSelectedCategory(actual.cost_category);
    setFromDate(actual.from_date);
    setToDate(actual.to_date);
    setEntryMethod(actual.entry_method || "manual");
    setDataSource(actual.data_source || "");
    setActualSpent(actual.actual_spent.toString());
    setNotes(actual.notes || "");
    setEditingActual(actual);
    
    // If it's a planned cost, fetch the cost item
    if (!actual.is_unplanned && actual.cost_item_id) {
      const costItem = costItems.find(item => item.id === actual.cost_item_id);
      if (costItem) {
        setSelectedCostItem(costItem);
        const contextData = await fetchCostItemContext(actual.cost_item_id);
        if (contextData) {
          setContext(contextData);
          setIsUnplannedCost(false);
        }
      }
    } else {
      // Unplanned cost
      setIsUnplannedCost(true);
      setContext({
        cost_item: {
          item_name: actual.item_name,
          cost_category: actual.cost_category,
          cost_type: actual.cost_type || 'one_time',
          amount: 0
        },
        budget: {
          budgeted_amount: 0,
          annual_budget: 0,
          ytd_budget: 0,
          ytd_spent: 0,
          remaining: 0
        }
      });
    }
    
    setShowActualsList(false);
    setCurrentStep(2);
  };
  
  const handleDeleteActual = (actual) => {
    setActualToDelete(actual);
    setShowDeleteDialog(true);
  };
  
  const confirmDeleteActual = async () => {
    if (!actualToDelete) return;
    
    try {
      setLoading(true);
      await axios.delete(`${API}/actual-data/costs-v2/${actualToDelete.id}`);
      toast.success("Cost actual deleted successfully!");
      await fetchSavedActuals();
      setShowDeleteDialog(false);
      setActualToDelete(null);
    } catch (error) {
      console.error("Error deleting cost actual:", error);
      toast.error(error.response?.data?.detail || "Failed to delete cost actual");
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateCostActual = async () => {
    if (!actualSpent || !fromDate || !toDate) {
      toast.error("Please enter actual spent amount and date range");
      return;
    }
    
    try {
      setLoading(true);
      
      const payload = {
        from_date: fromDate,
        to_date: toDate,
        entry_method: entryMethod,
        data_source: dataSource || null,
        actual_spent: parseFloat(actualSpent),
        notes
      };
      
      await axios.put(`${API}/actual-data/costs-v2/${editingActual.id}`, payload);
      
      toast.success("Cost actual updated successfully!");
      
      await fetchSavedActuals();
      
      // Reset form
      setCurrentStep(1);
      setSelectedCategory("");
      setSelectedCostItem(null);
      setContext(null);
      setIsUnplannedCost(false);
      setEditingActual(null);
      setActualSpent("");
      setNotes("");
      setDataSource("");
      setBudgetVariance(null);
      setYtdVariance(null);
      
    } catch (error) {
      console.error("Error updating cost actual:", error);
      toast.error(error.response?.data?.detail || "Failed to update cost actual");
    } finally {
      setLoading(false);
    }
  };

  
  const formatCurrency = (amount) => {
    return formatCurrencyContext(amount || 0, currentPlan?.currency);
  };
  
  const getCategoryLabel = (value) => {
    const cat = COST_CATEGORIES.find(c => c.value === value);
    return cat ? cat.label : value;
  };
  
  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Cost Category Selection */}
      <Card>
        <CardHeader>
          <Tooltip content="Select cost category to filter cost items. Categories: Fixed (rent, salaries), Variable (materials, commissions), OpEx (operating expenses), CapEx (capital investments), COGS (direct production costs). Choose category first to narrow down options.">
            <CardTitle className="flex items-center">
              <Building2 className="h-5 w-5 mr-2 text-purple-600" />
              Select Cost Category
            </CardTitle>
          </Tooltip>
          <CardDescription>Choose the category to filter cost items</CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger>
              <SelectValue placeholder="Select cost category..." />
            </SelectTrigger>
            <SelectContent>
              {COST_CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>
      
      {selectedCategory && (
        <>
          {/* Date Range Selection */}
          <Card>
            <CardHeader>
              <Tooltip content="Time period when cost was incurred. Use accrual basis (when service received) not cash basis (when paid). Example: Dec electricity bill paid Jan 5 → Record in Dec. Align with budget periods for accurate variance.">
                <CardTitle className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-blue-600" />
                  Cost Period (Date Range)
                </CardTitle>
              </Tooltip>
              <CardDescription>Specify the date range this cost data represents</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Tooltip content="Cost period start date. Use first day of billing cycle for recurring costs. Example: Monthly rent → From = 1st of month. One-time purchase → Actual date. Must be ≤ To Date and ≤ Today.">
                    <Label>From Date *</Label>
                  </Tooltip>
                  <Input 
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    max={toDate}
                  />
                </div>
                
                <div>
                  <Tooltip content="Cost period end date. Last day of billing cycle. Example: January costs → To = Jan 31. One-time expense → Same as From Date. Cannot be future date. Must be ≥ From Date.">
                    <Label>To Date *</Label>
                  </Tooltip>
                  <Input 
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    min={fromDate}
                  />
                </div>
              </div>
              
              {fromDate && toDate && (
                <div className="bg-blue-50 rounded p-3">
                  <p className="text-sm font-medium text-blue-900">
                    Period: {getPeriodLabel()} ({getDaysInRange()} days)
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    {new Date(fromDate).toLocaleDateString()} - {new Date(toDate).toLocaleDateString()}
                  </p>
                </div>
              )}
              
              <div className="border-t pt-4 space-y-3">
                <div>
                  <Tooltip content="How cost data was collected. Manual = Type directly. Automated = Auto-synced from accounting system (QuickBooks, Xero). Imported = CSV/Excel upload. Document for audit trail.">
                    <Label>Entry Method</Label>
                  </Tooltip>
                  <Select value={entryMethod} onValueChange={setEntryMethod}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual Entry</SelectItem>
                      <SelectItem value="automated">Automated (Real-time)</SelectItem>
                      <SelectItem value="imported">Imported from System</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {entryMethod !== "manual" && (
                  <div>
                    <Tooltip content="System where cost data originated. Examples: QuickBooks, ADP (payroll), Expensify (expenses), AWS (cloud costs). Used for reconciliation and audit. Specify exact system name for tracking.">
                      <Label>Data Source (Optional)</Label>
                    </Tooltip>
                    <Input 
                      value={dataSource}
                      onChange={(e) => setDataSource(e.target.value)}
                      placeholder="e.g., Accounting System, ERP, Payroll, etc."
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Cost Item Selection */}
          <Card>
            <CardHeader>
              <Tooltip content="Select specific cost item from your Cost Management plan. Links actual spending to budget. If cost not in budget, use 'Record Unplanned Cost' option below.">
                <CardTitle className="flex items-center">
                  <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                  Select Cost Item from {getCategoryLabel(selectedCategory)}
                </CardTitle>
              </Tooltip>
              <CardDescription>Choose the specific cost item to record</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-600">Loading cost items...</p>
                </div>
              ) : (
                <>
                  {/* Unplanned Cost Option */}
                  <Tooltip content="Record costs not in original budget (emergency repairs, unexpected legal fees, unplanned consulting). Creates ad-hoc entry without pre-existing budget.">
                    <div 
                      className="border-2 border-dashed border-orange-400 rounded-lg p-4 mb-4 cursor-pointer hover:bg-orange-50 transition-all"
                      onClick={handleUnplannedCostSelect}
                    >
                      <div className="flex items-center">
                        <Plus className="h-6 w-6 text-orange-600 mr-3" />
                        <div>
                          <h3 className="font-semibold text-orange-900">Record Unplanned/Unforeseen Cost</h3>
                          <p className="text-sm text-orange-700">Enter costs that weren't budgeted (emergency expenses, unexpected fees, etc.)</p>
                        </div>
                      </div>
                    </div>
                  </Tooltip>
                  
                  {/* Planned Cost Items */}
                  {costItems.length === 0 ? (
                    <div className="text-center py-8">
                      <TrendingDown className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No planned cost items found in {getCategoryLabel(selectedCategory)}</p>
                      <p className="text-sm text-gray-500 mt-2">You can still record unplanned costs using the option above</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">Planned Cost Items:</p>
                      {costItems.map((item) => (
                    <div
                      key={item.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${
                        selectedCostItem?.id === item.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                      }`}
                      onClick={() => handleCostItemSelect(item)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <h3 className="font-semibold text-lg">{item.item_name}</h3>
                            <Badge variant="outline" className="ml-2">{getCategoryLabel(item.cost_category)}</Badge>
                          </div>
                          
                          <div className="bg-green-50 rounded p-3 mb-2">
                            <p className="text-sm font-semibold text-green-900 mb-2">📊 CM Input Data (From Cost Management):</p>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-gray-600">Budget:</span>
                                <span className="ml-2 font-medium">
                                  {(() => {
                                    console.log('Cost item display:', {
                                      name: item.item_name,
                                      cost_type: item.cost_type,
                                      amount: item.amount,
                                      calculated_amount: item.calculated_amount
                                    });
                                    
                                    if (item.cost_type === 'per_unit' || item.cost_type === 'percentage_of_revenue') {
                                      return formatCurrency(item.calculated_amount || item.amount);
                                    }
                                    return formatCurrency(item.amount);
                                  })()}
                                </span>
                                {item.cost_type === 'per_unit' && item.calculated_amount && (
                                  <span className="text-gray-500 text-xs ml-1">
                                    ({formatCurrency(item.amount)}/unit)
                                  </span>
                                )}
                                {item.cost_type === 'percentage_of_revenue' && item.calculated_amount && (
                                  <span className="text-gray-500 text-xs ml-1">
                                    ({item.amount}%)
                                  </span>
                                )}
                              </div>
                              <div>
                                <span className="text-gray-600">Cost Type:</span>
                                <span className="ml-2 font-medium capitalize">{item.cost_type?.replace('_', ' ')}</span>
                              </div>
                              {item.department && (
                                <div>
                                  <span className="text-gray-600">Department:</span>
                                  <span className="ml-2 font-medium">{item.department}</span>
                                </div>
                              )}
                              {item.owner && (
                                <div>
                                  <span className="text-gray-600">Owner:</span>
                                  <span className="ml-2 font-medium">{item.owner}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {(item.linked_objective_id || item.linked_initiative_id) && (
                            <div className="text-xs text-blue-600">
                              <span className="font-medium">🎯 Strategic Link:</span>
                              {item.objective_info && <span className="ml-2">{item.objective_info.objective_id}</span>}
                              {item.initiative_info && <span className="ml-2">→ {item.initiative_info.initiative_id}</span>}
                            </div>
                          )}
                        </div>
                        
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCostItemSelect(item);
                          }}
                        >
                          Select
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
  
  const renderStep2 = () => (
    <div className="space-y-6">
      {/* Period Info */}
      <Card className="bg-purple-50 border-purple-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-4">
              {editingActual && (
                <Badge variant="default" className="bg-blue-600">
                  Editing Entry
                </Badge>
              )}
              <div>
                <span className="text-gray-600">Category:</span>
                <span className="ml-2 font-semibold">{getCategoryLabel(selectedCategory)}</span>
              </div>
              {isUnplannedCost && (
                <Badge variant="outline" className="bg-orange-100 text-orange-900 border-orange-400">
                  Unplanned Cost
                </Badge>
              )}
              <div>
                <span className="text-gray-600">Period:</span>
                <span className="ml-2 font-semibold">{getPeriodLabel()} ({getDaysInRange()} days)</span>
              </div>
              <div>
                <span className="text-gray-600">From:</span>
                <span className="ml-2 font-semibold">{fromDate}</span>
              </div>
              <div>
                <span className="text-gray-600">To:</span>
                <span className="ml-2 font-semibold">{toDate}</span>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setCurrentStep(1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Change
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Strategic Framework Linkage */}
      {(context?.objective || context?.initiative || context?.revenue_stream) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Target className="h-5 w-5 mr-2 text-blue-600" />
              Strategic Framework Linkage
            </CardTitle>
            <CardDescription>Strategic hierarchy for {selectedCostItem?.item_name}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 bg-gray-50 rounded-lg p-4">
              {context?.objective && (
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-center mb-1">
                    <Building2 className="h-4 w-4 mr-2 text-blue-600" />
                    <span className="font-semibold">Objective: {context.objective.name}</span>
                    <Badge variant="outline" className="ml-2 text-xs">{context.objective.objective_id}</Badge>
                  </div>
                  <div className="text-sm text-gray-600">
                    Owner: {context.objective.owner || 'N/A'}
                  </div>
                </div>
              )}
              
              {context?.initiative && (
                <div className="border-l-4 border-green-500 pl-4 ml-4">
                  <div className="flex items-center mb-1">
                    <Lightbulb className="h-4 w-4 mr-2 text-green-600" />
                    <span className="font-semibold">Initiative: {context.initiative.name}</span>
                    <Badge variant="outline" className="ml-2 text-xs">{context.initiative.initiative_id}</Badge>
                  </div>
                  <div className="text-sm text-gray-600">
                    Owner: {context.initiative.owner || 'N/A'} | Budget: {formatCurrency(context.initiative.budget)}
                  </div>
                </div>
              )}
              
              {context?.revenue_stream && (
                <div className="border-l-4 border-purple-500 pl-4 ml-8">
                  <div className="flex items-center mb-1">
                    <DollarSign className="h-4 w-4 mr-2 text-purple-600" />
                    <span className="font-semibold">Revenue Stream: {context.revenue_stream.name}</span>
                  </div>
                </div>
              )}
              
              {context?.products && context.products.length > 0 && (
                <div className="border-l-4 border-orange-500 pl-4 ml-12">
                  <div className="flex items-center mb-1">
                    <Package className="h-4 w-4 mr-2 text-orange-600" />
                    <span className="font-semibold">Supports Products:</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {context.products.map(p => p.name).join(', ')}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Budget vs Actual Comparison */}
      <Card>
        <CardHeader>
          <Tooltip content="Compare budgeted (planned) vs actual (spent) amounts. Left = Budget limits. Right = Actual spending. Bottom = Variance. For COSTS: Negative % (green) = Under budget (good!), Positive % (red) = Over budget (concern).">
            <CardTitle className="flex items-center">
              <TrendingDown className="h-5 w-5 mr-2 text-red-600" />
              Budget vs Actual Entry
            </CardTitle>
          </Tooltip>
          <CardDescription>Enter actual cost and compare with CM planning data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* CM Planning Data */}
            <div>
              <Tooltip content="Your approved budget from Cost Management. Shows spending limits and tracking metrics for this cost item.">
                <h3 className="font-semibold mb-4 flex items-center text-gray-700">
                  📊 CM Planning Data
                </h3>
              </Tooltip>
              <div className="space-y-3 bg-green-50 rounded-lg p-4">
                <div>
                  <Tooltip content="Budget for current period (monthly/quarterly). Your spending limit from Cost Management plan. Example: $5,000 monthly rent budget.">
                    <Label className="text-sm text-gray-600">Budget (Period)</Label>
                  </Tooltip>
                  <div className="text-2xl font-bold text-green-700">
                    {formatCurrency(context?.budget?.budgeted_amount)}
                  </div>
                </div>
                <div>
                  <Tooltip content="Cost classification: Fixed (unchanging), Variable (usage-based), OpEx (operational), CapEx (capital), COGS (production), etc.">
                    <Label className="text-sm text-gray-600">Cost Type</Label>
                  </Tooltip>
                  <div className="text-lg font-semibold text-green-700 capitalize">
                    {context?.cost_item?.cost_type?.replace('_', ' ')}
                  </div>
                </div>
                <div>
                  <Tooltip content="Total yearly budget for this cost. Formula: Monthly Budget × 12. Example: $5K/month × 12 = $60K annual. Used for YTD tracking.">
                    <Label className="text-sm text-gray-600">Annual Budget</Label>
                  </Tooltip>
                  <div className="text-lg font-semibold text-green-700">
                    {formatCurrency(context?.budget?.annual_budget)}
                  </div>
                </div>
                
                <div className="border-t border-green-200 pt-3 mt-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <Tooltip content="Year-to-date budget from Jan 1 to now. Formula: Monthly Budget × Months Elapsed. Example: $5K × 3 months = $15K YTD budget.">
                        <Label className="text-xs text-gray-600">YTD Budget</Label>
                      </Tooltip>
                      <div className="font-semibold text-green-700">
                        {formatCurrency(context?.budget?.ytd_budget)}
                      </div>
                    </div>
                    <div>
                      <Tooltip content="Total already spent year-to-date before this entry. Example: Jan $5K + Feb $5.2K = $10.2K YTD spent. Updates after saving.">
                        <Label className="text-xs text-gray-600">YTD Spent</Label>
                      </Tooltip>
                      <div className="font-semibold text-green-700">
                        {formatCurrency(context?.budget?.ytd_spent)}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <Tooltip content="Budget remaining for rest of year. Formula: Annual Budget - YTD Spent. Example: $60K - $10.2K = $49.8K remaining. Tracks burn rate.">
                        <Label className="text-xs text-gray-600">Remaining</Label>
                      </Tooltip>
                      <div className="font-semibold text-green-700">
                        {formatCurrency(context?.budget?.remaining)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Actual Data Entry */}
            <div>
              <Tooltip content="Enter actual amount spent in this period. Real spending vs budget. Use exact amounts including cents.">
                <h3 className="font-semibold mb-4 flex items-center text-gray-700">
                  ✍️ Actual (Enter)
                </h3>
              </Tooltip>
              <div className="space-y-4">
                <div>
                  <Tooltip content="Total amount spent in this period. Enter exact dollar amount. Example: $5,234.67 for January rent. Must be ≥ 0. Currency handled automatically.">
                    <Label>Actual Spent *</Label>
                  </Tooltip>
                  <Input 
                    type="number"
                    value={actualSpent}
                    onChange={(e) => setActualSpent(e.target.value)}
                    placeholder="Enter actual amount spent"
                    className="text-lg"
                  />
                </div>
                
                {actualSpent && (
                  <div className="bg-blue-50 rounded p-3 space-y-2 text-sm">
                    <div>
                      <Tooltip content="Updated YTD total after adding this entry. Formula: Current YTD + This Entry. Example: $10.2K + $5K = $15.2K. Preview before saving.">
                        <span className="text-gray-600">New YTD Spent:</span>
                      </Tooltip>
                      <span className="ml-2 font-semibold">
                        {formatCurrency((context?.budget?.ytd_spent || 0) + parseFloat(actualSpent))}
                      </span>
                    </div>
                    <div>
                      <Tooltip content="Updated budget remaining after this entry. Formula: Annual Budget - New YTD. Example: $60K - $15.2K = $44.8K remaining. Alerts if low.">
                        <span className="text-gray-600">New Remaining:</span>
                      </Tooltip>
                      <span className="ml-2 font-semibold">
                        {formatCurrency((context?.budget?.remaining || 0) - parseFloat(actualSpent))}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Variance Preview */}
          {(budgetVariance !== null || ytdVariance !== null) && (
            <div className="mt-6 p-4 bg-purple-50 rounded-lg">
              <Tooltip content="Real-time variance: spending vs budget. For costs, Negative % (green) = Under budget (saved money!), Positive % (red) = Over budget (investigate).">
                <h3 className="font-semibold mb-3 flex items-center text-gray-700">
                  📊 Variance Preview (Real-time)
                </h3>
              </Tooltip>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Tooltip content="Period spending vs budget gap. Formula: ((Actual - Budget) / Budget) × 100. Example: Actual $5.5K, Budget $5K = +10% over (red). Negative = under budget (green).">
                    <Label className="text-sm text-gray-600">Budget Variance</Label>
                  </Tooltip>
                  <div className={`text-xl font-bold ${
                    parseFloat(budgetVariance) <= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {budgetVariance}%
                  </div>
                </div>
                <div>
                  <Tooltip content="Year-to-date spending vs budget gap. Formula: ((YTD Spent - YTD Budget) / YTD Budget) × 100. Example: Spent $16K vs Budget $15K = +6.7% over. Tracks annual trend.">
                    <Label className="text-sm text-gray-600">YTD Variance</Label>
                  </Tooltip>
                  <div className={`text-xl font-bold ${
                    parseFloat(ytdVariance) <= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {ytdVariance}%
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Notes */}
          <div className="mt-6">
            <Label>Notes (Optional)</Label>
            <Textarea 
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add contextual notes about this cost entry..."
              rows={3}
            />
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center justify-between mt-6 pt-6 border-t">
            <Button 
              variant="outline" 
              onClick={() => setCurrentStep(1)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button 
              onClick={editingActual ? handleUpdateCostActual : handleSaveCostActual}
              disabled={loading || !actualSpent}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? "Saving..." : editingActual ? "Update Actual" : "Save Actual"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
  
  const renderActualsList = () => (
    <Card>
      <CardHeader>
        <CardTitle>Recorded Cost Actuals</CardTitle>
        <CardDescription>All cost actuals recorded for this plan</CardDescription>
      </CardHeader>
      <CardContent>
        {savedActuals.length === 0 ? (
          <div className="text-center py-12">
            <TrendingDown className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No cost actuals recorded yet</p>
            <Button className="mt-4" onClick={() => setShowActualsList(false)}>
              <Plus className="h-4 w-4 mr-2" />
              Record First Actual
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {savedActuals.map((actual, index) => (
              <div key={actual.id || index} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h3 className="font-semibold text-lg">{actual.item_name}</h3>
                      <Badge variant="outline" className="ml-2">{getCategoryLabel(actual.cost_category)}</Badge>
                      <Badge 
                        variant={actual.status === "On Track" ? "default" : actual.status === "Over Budget" ? "destructive" : "secondary"}
                        className="ml-2"
                      >
                        {actual.status}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Period:</span>
                        <p className="font-medium">
                          {actual.from_date && actual.to_date 
                            ? `${new Date(actual.from_date).toLocaleDateString()} - ${new Date(actual.to_date).toLocaleDateString()}`
                            : actual.period || actual.date
                          }
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">Actual Spent:</span>
                        <p className="font-medium">{formatCurrency(actual.actual_spent)}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Budget:</span>
                        <p className="font-medium">{formatCurrency(actual.budgeted_amount)}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">YTD Spent:</span>
                        <p className="font-medium">{formatCurrency(actual.ytd_spent_after)}</p>
                      </div>
                    </div>
                    
                    <div className="mt-3 flex items-center space-x-4 text-sm">
                      <div>
                        <span className="text-gray-600">Budget Variance:</span>
                        <span className={`ml-2 font-semibold ${
                          actual.budget_variance_pct <= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {actual.budget_variance_pct}%
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">YTD Variance:</span>
                        <span className={`ml-2 font-semibold ${
                          actual.ytd_variance_pct <= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {actual.ytd_variance_pct}%
                        </span>
                      </div>
                    </div>
                    
                    {actual.notes && (
                      <p className="mt-2 text-sm text-gray-600 italic">{actual.notes}</p>
                    )}
                    
                    <p className="mt-2 text-xs text-gray-500">
                      Recorded: {new Date(actual.created_at).toLocaleString()}
                      {actual.entry_method && ` | Entry: ${actual.entry_method}`}
                      {actual.data_source && ` | Source: ${actual.data_source}`}
                    </p>
                  </div>
                  
                  {/* Edit and Delete Buttons */}
                  <div className="flex flex-col space-y-2 ml-4">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEditActual(actual)}
                      className="flex items-center"
                    >
                      <Edit2 className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDeleteActual(actual)}
                      className="flex items-center text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Tooltip 
            content="Record your actual cost expenditures to track spending against budget plans. This data helps monitor cost control and identify variances from Cost Management targets."
            position="bottom"
          >
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <TrendingDown className="h-8 w-8 mr-3 text-red-600" />
              Record Cost Actuals
            </h1>
          </Tooltip>
          <p className="text-gray-600 mt-2">
            {showActualsList 
              ? `Viewing ${savedActuals.length} recorded actuals` 
              : currentStep === 1 
                ? "Select cost category, item, and configure date range" 
                : "Enter actual cost data and compare with CM planning"
            }
          </p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
          {!showActualsList && (
            <>
              <Tooltip content="View all previously recorded cost entries. Filter, edit, or delete past cost actuals for this category.">
                <Button 
                  variant="outline" 
                  onClick={() => setShowActualsList(true)}
                >
                  View Recorded Actuals ({savedActuals.length})
                </Button>
              </Tooltip>
              {/* Step Indicator */}
              <div className="flex items-center space-x-2 ml-4">
                <Tooltip content="Step 1: Select cost category, item, and date range">
                  <Badge variant={currentStep === 1 ? "default" : "outline"}>Step 1</Badge>
                </Tooltip>
                <ArrowRight className="h-4 w-4 text-gray-400" />
                <Tooltip content="Step 2: Enter actual spent amount and view budget variance">
                  <Badge variant={currentStep === 2 ? "default" : "outline"}>Step 2</Badge>
                </Tooltip>
              </div>
            </>
          )}
          {showActualsList && (
            <Tooltip content="Start recording a new cost actual entry">
              <Button onClick={() => setShowActualsList(false)}>
                <Plus className="h-4 w-4 mr-2" />
                Record New Actual
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
      
      {/* Current Step Content or Actuals List */}
      {showActualsList ? renderActualsList() : (currentStep === 1 ? renderStep1() : renderStep2())}
      
      {/* Unplanned Cost Dialog - Uses CM Form */}
      <Dialog open={showUnplannedDialog} onOpenChange={setShowUnplannedDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Plus className="h-5 w-5 mr-2 text-orange-600" />
              Record Unplanned/Unforeseen Cost
            </DialogTitle>
            <DialogDescription>
              Use the Cost Management form to capture all details for this unbudgeted expense
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <CostFormContent
              form={unplannedCostForm}
              setForm={setUnplannedCostForm}
              category={selectedCategory}
              costLibraries={costLibraries}
              sfHierarchy={sfHierarchy}
              financialLoans={financialLoans}
              products={products}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnplannedDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveUnplannedCost}
              disabled={!unplannedCostForm.item_name || !unplannedCostForm.amount}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Continue to Record Actual
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <Trash2 className="h-5 w-5 mr-2" />
              Delete Cost Actual
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this cost actual entry? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {actualToDelete && (
            <div className="py-4 space-y-2">
              <p className="font-semibold">{actualToDelete.item_name}</p>
              <p className="text-sm text-gray-600">
                Period: {actualToDelete.from_date && actualToDelete.to_date 
                  ? `${new Date(actualToDelete.from_date).toLocaleDateString()} - ${new Date(actualToDelete.to_date).toLocaleDateString()}`
                  : actualToDelete.period || actualToDelete.date
                }
              </p>
              <p className="text-sm text-gray-600">
                Actual Spent: {formatCurrency(actualToDelete.actual_spent)}
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteDialog(false);
                setActualToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteActual}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CostActualsEntry;
